import * as React from 'react';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Alert from '@mui/material/Alert';
import axios from 'axios';
import TextInput from '../../zeon-react-ui/input/TextInput';
import { useAuth } from '../../zeon-react-ui/auth/model';

/**
 * PARAMS SELECT
 */
export interface IParamStringAction {
  (v: string): void;
}
export interface IParamBoolAction {
  (v: boolean): void;
}
interface ILoadedAction {
  (loaded: boolean): void;
}
interface IParamSelectProps {
  site: string;
  location: string;
  collection: string;
  commodity: string;
  sku: string;
  timing: boolean;
  quantity: boolean;
  onLocation: IParamStringAction;
  onCollection: IParamStringAction;
  onCommodity: IParamStringAction;
  onSKU: IParamStringAction;
  onTiming: IParamBoolAction;
  onQuantity: IParamBoolAction;
  onLoaded?: ILoadedAction;
}
interface IParamLocation { code: string; description: string; }
interface IParamCommodity { code: string; description: string; }
interface IParamCollection { code: string; description: string; }
interface IParamTiming { code: string; description: string; }
interface IParamQuantity { code: string; description: string; }

const ParamSelect = (props: IParamSelectProps) => {
  const [loading, setLoading] = React.useState(false);
  const [loaded, setDataLoaded] = React.useState("");
  const [error, setError] = React.useState("");
  const loadingCircle = <Container sx={{ mt: 5, width: "80px" }}><CircularProgress /></Container>
  const errorAlert = <Alert severity="error">{error}</Alert>
  const setLoaded = (v: string) => {
    setDataLoaded(v);
    if (props.onLoaded) props.onLoaded(v != '');
  }
  const getData = () => {
    setLoading(true);
    axios.get(`/api/dismag/param-lookup/${props.site}`).then(answer => {
      if (answer && answer.data.success) {
        const allCommodities = defaultCommodities.concat(answer.data.payload["commodities"] as IParamCommodity[]);
        const allCollections = defaultCollections.concat(answer.data.payload["collections"] as IParamCollection[]);
        const allLocations = defaultLocations.concat(answer.data.payload["locations"] as IParamCollection[]);
        setLocations(allLocations as []);
        setCommodities(allCommodities as []);
        setCollections(allCollections as []);
      } else {
        setError(answer.data.payload.message);
      }
      setLoaded(props.site);
      setLoading(false);
    }).catch(e => {
      console.error(e);
      setError(`${e}`);
      setLoading(false);
      setLoaded("");
    });
    return loadingCircle;
  }
  const auth = useAuth();
  const [locations, setLocations] = React.useState([]);
  const [commodities, setCommodities] = React.useState([]);
  const [collections, setCollections] = React.useState([]);

  const defaultCommodities: IParamCommodity[] = auth?.appdata?.ifs["commodities"].length && (auth?.appdata?.ifs["commodities"][0] === "*") ? [
    { code: "*", description: "All Groups" },
    { code: "?", description: "Without Group" },
    { code: "@", description: "KIT" }
  ] : [];
  const defaultCollections: IParamCollection[] = [
    { code: "*", description: "All Collections" }
  ];
  const defaultLocations: IParamLocation[] = [];
  const allQuantities = [
    { code: "0", description: "Show as > 10" },
    { code: "1", description: "Real Quantity" }
  ]
  const allTimings = [
    { code: "0", description: "Totals on last date" },
    { code: "1", description: "Date details" }
  ]

  if (error) return errorAlert;
  else if (loaded && !loading && (loaded !== props.site)) return getData();
  else if (!loading && !loaded) return getData();
  else if (!loaded) return loadingCircle;

  return <Container maxWidth="xs" sx={{ mt: 1 }}>
    <Grid container spacing={2} sx={{ mt: 0 }}>
      <TextInput label="SKU" value={props.sku} onChange={props.onSKU} />
    </Grid>
    <Grid container spacing={2} sx={{ mt: 0 }}>
      <FormControl variant="standard" sx={{ minWidth: 180 }} fullWidth>
        <InputLabel id="location-select-label">Location</InputLabel>
        <Select labelId="location-select-label" id="location-select"
          value={locations.length ? props.location : null}
          label={locations.length ? (locations as IParamLocation[]).reduce((out: string, one) => {
            if ((one as IParamLocation).code === props.location) out = (one as IParamLocation).description;
            return out;
          }, "N/A") : null}
          onChange={(e) => { props.onLocation(`${e["target"].value}`); }}
        >
          {commodities.length ? (locations as IParamLocation[]).map((one, index) => {
            const { code, description } = (one as IParamLocation);
            return <MenuItem key={`location-lookup-${index}`} value={code}>{description}</MenuItem>;
          }) : null}
        </Select>
      </FormControl>
    </Grid>
    <Grid container spacing={2} sx={{ mt: 2 }}>
      <FormControl variant="standard" sx={{ minWidth: 180 }} fullWidth>
        <InputLabel id="commodity-select-label">Commodity Group</InputLabel>
        <Select labelId="commodity-select-label" id="commodity-select"
          value={commodities.length ? props.commodity : null}
          label={commodities.length ? (commodities as IParamCommodity[]).reduce((out: string, one) => {
            if ((one as IParamCommodity).code === props.commodity) out = (one as IParamCommodity).description;
            return out;
          }, "N/A") : null}
          onChange={(e) => { props.onCommodity(`${e["target"].value}`); }}
        >
          {commodities.length ? (commodities as IParamCommodity[]).map((one, index) => {
            const { code, description } = (one as IParamCommodity);
            const showCode = ['*', '?', '@'].indexOf(code) === -1;
            return <MenuItem key={`commodity-lookup-${index}`} value={code}>{showCode ? code : ''}{showCode ? ' - ' : ''}{description}</MenuItem>;
          }) : null}
        </Select>
      </FormControl>
    </Grid>
    <Grid container spacing={2} sx={{ mt: 2 }}>
      <FormControl variant="standard" sx={{ minWidth: 180 }} fullWidth>
        <InputLabel id="collection-select-label">Collection</InputLabel>
        <Select labelId="collection-select-label" id="collection-select"
          value={collections.length ? props.collection : null}
          label={collections.length ? (collections as IParamCollection[]).reduce((out: string, one) => {
            if ((one as IParamCollection).code === props.collection) out = (one as IParamCollection).description;
            return out;
          }, "N/A") : null}
          onChange={(e) => { props.onCollection(`${e["target"].value}`); }}
        >
          {collections.length ? (collections as IParamCollection[]).map((one, index) => {
            const { code, description } = (one as IParamCollection);
            return <MenuItem key={`collection-lookup-${index}`} value={code}>{description}</MenuItem>;
          }) : null}
        </Select>
      </FormControl>
    </Grid>
    {/*
    <Grid container spacing={2} sx={{ mt: 2 }}>
      <FormControl variant="standard" sx={{ minWidth: 180 }} fullWidth>
        <InputLabel id="timing-select-label">Timing</InputLabel>
        <Select labelId="timing-select-label" id="timing-select"
          value={props.timing ? "1" : "0"}
          label={allTimings[props.timing ? 1 : 0].description}
          onChange={(e) => { props.onTiming(e["target"].value === "0" ? false : true); }}
        >
          {allTimings.length ? (allTimings as IParamTiming[]).map((one, index) => {
            const { code, description } = (one as IParamTiming);
            return <MenuItem key={`timing-lookup-${index}`} value={code}>{description}</MenuItem>;
          }) : null}
        </Select>
      </FormControl>
    </Grid>
    <Grid container spacing={2} sx={{ mt: 2 }}>
      <FormControl variant="standard" sx={{ minWidth: 180 }} fullWidth>
        <InputLabel id="quantity-select-label">Quantity</InputLabel>
        <Select labelId="quantity-select-label" id="quantity-select"
          value={props.quantity ? "1" : "0"}
          label={allQuantities[props.quantity ? 1 : 0].description}
          onChange={(e) => { props.onQuantity(e["target"].value === "0" ? false : true); }}>
          {allQuantities.length ? (allQuantities as IParamTiming[]).map((one, index) => {
            const { code, description } = (one as IParamTiming);
            return <MenuItem key={`quantity-lookup-${index}`} value={code}>{description}</MenuItem>;
          }) : null}
        </Select>
      </FormControl>
    </Grid>
        */}
  </Container>
}

export default ParamSelect;